import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import { debounce } from 'lodash';

function EntradaList() {
  const navigate = useNavigate();

  // Filtros
  const [referencia, setReferencia] = useState('');
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [status, setStatus] = useState('');
  const [idFornecedor, setIdFornecedor] = useState('');

  // Lista e Paginação
  const [notas, setNotas] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [loading, setLoading] = useState(false);

  // Config
  const registrosPorPagina = 10;
  const API_URL = '/api/EstNotaFiscalEntradas';

  // Função para buscar do backend
  const buscarNotas = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/buscar`, {
        params: {
          referencia,
          dataInicial,
          dataFinal,
          status,
          idFornecedor: idFornecedor ? parseInt(idFornecedor) : null,
          page: pagina - 1, // zero-based
          size: registrosPorPagina
        }
      });
      setNotas(data.content);
      setTotalPaginas(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar Notas de Entrada:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar chamadas excessivas
  const debouncedBuscar = useCallback(debounce(() => {
    buscarNotas();
  }, 500), [
    referencia, dataInicial, dataFinal, status, idFornecedor, pagina
  ]);

  // useEffect para disparar busca automática
  useEffect(() => {
    debouncedBuscar();
    return debouncedBuscar.cancel;
  }, [referencia, dataInicial, dataFinal, status, idFornecedor, pagina, debouncedBuscar]);

  // Paginação
  const handleChangePagina = (event, value) => {
    setPagina(value);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setReferencia('');
    setDataInicial('');
    setDataFinal('');
    setStatus('');
    setIdFornecedor('');
    setPagina(1);
  };

  // Botões
  const handleNovo = () => {
    navigate('/entrada/edit'); // Ajuste conforme rota real
  };

  const handleEditar = (id) => {
    navigate(`/entrada/edit/${id}`);
  };

  const handleExcluir = async (id) => {
    console.log('Excluir nota ID:', id);
    // Exemplo:
    // await axios.delete(`${API_URL}/${id}`);
    // buscarNotas();
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      {/* Backdrop */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Título */}
      <Typography variant="h4" align="center" gutterBottom>
        Notas Fiscais de Entrada
      </Typography>

      {/* Filtros em Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} alignItems="center">
            {/* Referência (Número da Nota) */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Ref (Número)"
                variant="outlined"
                size="small"
                fullWidth
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
              />
            </Grid>
            {/* Data Inicial */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Data Inicial"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={dataInicial}
                onChange={(e) => setDataInicial(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {/* Data Final */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Data Final"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={dataFinal}
                onChange={(e) => setDataFinal(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {/* Status */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Status"
                variant="outlined"
                size="small"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              />
            </Grid>

            {/* Fornecedor */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="ID Fornecedor"
                variant="outlined"
                size="small"
                fullWidth
                value={idFornecedor}
                onChange={(e) => setIdFornecedor(e.target.value)}
              />
            </Grid>
            {/* Botão Limpar */}
            <Grid item xs={6} sm={1}>
              <Button variant="outlined" onClick={limparFiltros} fullWidth>
                Limpar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botão "+ Novo" */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleNovo}>
          + Novo
        </Button>
      </Box>

      {/* Tabela de Resultados */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Número</strong></TableCell>
              <TableCell><strong>Data Emissão</strong></TableCell>
              <TableCell><strong>Data Movimento</strong></TableCell>
              <TableCell><strong>Fornecedor</strong></TableCell>
              <TableCell><strong>Valor Total</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhuma nota encontrada.
                </TableCell>
              </TableRow>
            ) : (
              notas.map((nota, index) => (
                <TableRow key={index}>
                  <TableCell>{nota.id}</TableCell>
                  <TableCell>{nota.numeroNotaFiscal}</TableCell>
                  {/* Ajuste formatação de datas conforme necessidade */}
                  <TableCell>{nota.dataEmissao ? new Date(nota.dataEmissao).toLocaleDateString() : ''}</TableCell>
                  <TableCell>{nota.dataMovimento ? new Date(nota.dataMovimento).toLocaleDateString() : ''}</TableCell>
                  <TableCell>{nota.fornecedorNome}</TableCell>
                  <TableCell>{nota.valorTotalNotaFiscal}</TableCell>
                  <TableCell>{nota.status}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => handleEditar(nota.id)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleExcluir(nota.id)}
                    >
                      Excluir
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPaginas}
          page={pagina}
          onChange={handleChangePagina}
          color="primary"
        />
      </Box>
    </Box>
  );
}

export default EntradaList;
